import React, { useEffect, useRef, useState } from 'react';
import { configs, downloadDoc } from '../../../assets/Config';
import { createCompanyFileGroup, deleteCompanyFile, deleteCompanyFileGroup, getCompanyFile, getCompanyFileGroups, getCompanyFiles, updateCompanyFilesSequence, uploadCompanyFile } from '../../../services/apis/companyManagement/CMApi';
import { useLogin } from '../../../services/contexts/AdminLoginContext';

const MAX_FILE_SIZE = 2 * 1024 * 1024
const PDF = ["application/pdf"]
const PPT = ["application/pdf", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"]
const IMAGES_AND_PDF = ["application/pdf", "image/jpeg", "image/jpg", "image/png"]

const yearField = { key: "year", label: "Year", type: "text", placeholder: "e.g. FY26" }
const fileNameField = { key: "file_name", label: "Name of the Doc", type: "text", placeholder: "e.g. Earning call transcript" }
const dateField = { key: "date", label: "Date", type: "date" }

// key is the backend route slug; sequence only where backend has a /sequence route.
// slots: several files uploaded together as one line item (records share data.line_item_id, tagged with data.doc_type).
// groupFields: entries are backend file groups, files are uploaded into a group.
export const companyFileTypes = [
    { key: "annual_report", label: "Annual Report", fields: [yearField, { ...fileNameField, label: "Name of the File", placeholder: "e.g. AR" }], sequence: true, accept: PDF },
    {
        key: "concall_docs", label: "Concall Docs", fields: [yearField, fileNameField], sequence: true,
        slots: [
            { key: "transcript", label: "Transcript", accept: PDF },
            { key: "summary", label: "Summary", accept: PDF },
            { key: "ppt", label: "PPT", accept: PPT }
        ]
    },
    { key: "filing_docs", label: "Filing Docs", fields: [yearField, fileNameField], sequence: true, accept: PDF },
    {
        key: "funding_doc", label: "Funding Docs", groupLabel: "Funding Round", accept: PDF,
        groupFields: [
            { key: "round_name", label: "Round Name", type: "text", placeholder: "e.g. Series C" },
            { key: "time", label: "Time", type: "text", placeholder: "e.g. March 2025" },
            { key: "amount_raised", label: "Amount Raised", type: "text", placeholder: "e.g. ₹500 Cr" },
            { key: "valuation", label: "Valuation", type: "text", placeholder: "e.g. ₹10,000 Cr" }
        ]
    },
    { key: "altius_research", label: "Altius Research", fields: [{ ...fileNameField, label: "File Name", placeholder: "File Name" }, dateField], sequence: true, accept: PDF },
    { key: "general_report", label: "General Report", fields: [{ ...fileNameField, label: "File Name", placeholder: "File Name" }], sequence: false, accept: IMAGES_AND_PDF },
]

function emptyValues(fields) {
    return fields.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {})
}

function describe(fields, data) {
    return fields.map((field) => data?.[field.key] || "-").join(" | ")
}

function validFile(file, accept) {
    if (!accept.includes(file.type)) {
        alert(`${file.name}: file type not allowed.`)
        return false
    }
    if (file.size > MAX_FILE_SIZE) {
        alert(`${file.name}: file size should not exceed 2MB.`)
        return false
    }
    return true
}

function useApiErrorHandler(setError) {
    const { logout, setLoginError } = useLogin()
    return (response, fallback) => {
        if (response && response.status === 401) {
            logout()
            setLoginError(configs.unauthorizedErrorMessage)
        } else {
            setError(response?.data?.error || fallback)
        }
    }
}

function FieldInputs({ fields, values, setValues }) {
    return fields.map((field) => <div className="form-group mb-2" key={field.key}>
        <label className="f-12 mb-1">{field.label}</label>
        <input type={field.type} className="form-control f-10" placeholder={field.placeholder} value={values[field.key]} onChange={(e) => {
            setValues({ ...values, [field.key]: e.target.value })
        }} />
    </div>)
}

function FilePicker({ label, accept, multiple, files, setFiles, inputRef }) {
    return <div className="form-group mb-2">
        <p className="pb-0 mb-0 f-12">
            <label className="mb-0 p-1 curPoint btn-primary">
                {label}
                <input type="file" ref={inputRef} hidden multiple={multiple} accept={accept.join(", ")} onChange={(e) => {
                    setFiles(Array.from(e.target.files).filter((file) => validFile(file, accept)))
                }} />
            </label>
            <br /> File to be uploaded: <span className="text-theme">{files.map((file) => file.name).join(", ")}</span>
        </p>
    </div>
}

// fetch a file as base64 data url and view / download it
function useFileActions(fileTypeKey, handleApiError) {
    const { token } = useLogin()
    const [busyId, setBusyId] = useState("")

    async function fetchFile(item) {
        let response = await getCompanyFile(token, fileTypeKey, item.id)
        if (response && response.status === 200 && typeof response.data === "string") {
            return response.data
        }
        handleApiError(response, "Failed to fetch file.")
        return null
    }

    async function viewFile(item) {
        const win = window.open("", "_blank")
        try {
            setBusyId(item.id)
            let data = await fetchFile(item)
            if (data && win) {
                let blob = await (await fetch(data)).blob()
                win.location.href = URL.createObjectURL(blob)
            } else if (win) {
                win.close()
            }
        } catch (error) {
            if (win) win.close()
            console.error(error)
        } finally {
            setBusyId("")
        }
    }

    async function downloadFile(item) {
        try {
            setBusyId(item.id)
            let data = await fetchFile(item)
            if (data) {
                downloadDoc((item.file_original_name || `${fileTypeKey}_${item.id}`).replace(/\.[^.]+$/, ""), data)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setBusyId("")
        }
    }

    return { busyId, setBusyId, viewFile, downloadFile }
}

function FileButtons({ item, label, actions }) {
    if (actions.busyId === item.id) return <i className="fas fa-spinner fa-spin mr-2"></i>
    return <span className="mr-3 text-nowrap">
        {label ? <span className="mr-1">{label}:</span> : null}
        <i className="fa fa-external-link mr-2 curPoint text-primary" title="View" onClick={() => actions.viewFile(item)}></i>
        <i className="fa fa-download curPoint text-primary" title="Download" onClick={() => actions.downloadFile(item)}></i>
    </span>
}

// group records into line items, keeping the backend (sequence) order
function toLineItems(files) {
    const items = []
    const byKey = {}
    files.forEach((file) => {
        const key = file.data?.line_item_id || `file_${file.id}`
        if (!byKey[key]) {
            byKey[key] = { key: key, data: file.data, files: [] }
            items.push(byKey[key])
        }
        byKey[key].files.push(file)
    })
    return items
}

export function CompanyFileList({ companyId, fileType, groupId }) {

    const { token, setShowSuccess } = useLogin()
    const fields = fileType.fields || []
    const slots = fileType.slots || [{ key: "", label: "Choose File", accept: fileType.accept, multiple: !!groupId }]

    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const [values, setValues] = useState(emptyValues(fields))
    const [slotFiles, setSlotFiles] = useState({})
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState("")
    const fileInputs = useRef({})
    const handleApiError = useApiErrorHandler(setError)
    const actions = useFileActions(fileType.key, handleApiError)

    const lineItems = toLineItems(files)

    useEffect(() => {
        setFiles([])
        setValues(emptyValues(fields))
        setSlotFiles({})
        setError("")
        if (companyId) {
            loadFiles()
        }
    }, [companyId, fileType.key, groupId])

    async function loadFiles() {
        try {
            setLoading(true)
            let response = await getCompanyFiles(token, fileType.key, companyId, groupId)
            if (response && response.status === 200) {
                const list = Array.isArray(response.data) ? response.data : []
                setFiles(list)
                return list
            }
            handleApiError(response, "Failed to load files.")
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
        return null
    }

    async function saveSequence(ordered) {
        let response = await updateCompanyFilesSequence(token, fileType.key, companyId, ordered.map((file) => file.id))
        if (response && response.status === 200) return true
        handleApiError(response, "Failed to update sequence.")
        return false
    }

    async function upload() {
        const selected = slots.flatMap((slot) => (slotFiles[slot.key] || []).map((file) => ({ slot, file })))
        if (selected.length === 0) {
            setError("Please select a file to upload.")
            return
        }
        try {
            setUploading(true)
            setError("")
            const previousIds = files.map((file) => file.id)
            const lineItemId = slots.length > 1 ? `${Date.now()}${Math.floor(Math.random() * 1000)}` : ""
            let failed = false
            for (const { slot, file } of selected) {
                let response = await uploadCompanyFile(token, fileType.key, companyId, { ...values, line_item_id: lineItemId, doc_type: slot.key }, file, groupId)
                if (!response || response.status !== 200) {
                    handleApiError(response, `Failed to upload ${file.name}.`)
                    failed = true
                    break
                }
            }

            let list = await loadFiles()
            if (list && fileType.sequence) {
                const added = list.filter((file) => !previousIds.includes(file.id))
                if (added.length) {
                    const ordered = [...added, ...list.filter((file) => previousIds.includes(file.id))]
                    if (await saveSequence(ordered)) setFiles(ordered)
                }
            }
            if (!failed) {
                setValues(emptyValues(fields))
                setSlotFiles({})
                Object.values(fileInputs.current).forEach((input) => { if (input) input.value = "" })
                setShowSuccess(true)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setUploading(false)
        }
    }

    async function deleteLineItem(item) {
        try {
            const name = fields.length ? describe(fields, item.data) : item.files[0]?.file_original_name
            if (!window.confirm(`Delete ${name}${item.files.length > 1 ? ` and its ${item.files.length} files` : ""}?`)) return
            actions.setBusyId(item.key)
            const deletedIds = []
            for (const file of item.files) {
                let response = await deleteCompanyFile(token, fileType.key, file.id)
                if (!response || response.status !== 200) {
                    handleApiError(response, "Failed to delete file.")
                    break
                }
                deletedIds.push(file.id)
            }
            setFiles(files.filter((file) => !deletedIds.includes(file.id)))
            if (deletedIds.length === item.files.length) setShowSuccess(true)
        } catch (error) {
            console.error(error)
        } finally {
            actions.setBusyId("")
        }
    }

    // move line item up (-1) or down (+1) and save the new sequence
    async function moveLineItem(index, direction) {
        const target = index + direction
        if (target < 0 || target >= lineItems.length) return
        const previous = files
        const reordered = [...lineItems];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]]
        const ordered = reordered.flatMap((item) => item.files)
        setFiles(ordered)
        try {
            if (!(await saveSequence(ordered))) setFiles(previous)
        } catch (error) {
            setFiles(previous)
            console.error(error)
        }
    }

    function slotLabel(file) {
        return slots.find((slot) => slot.key && slot.key === file.data?.doc_type)?.label || ""
    }

    return (
        <div>
            <div>
                <FieldInputs fields={fields} values={values} setValues={setValues} />
                {slots.map((slot) => <FilePicker key={slot.key} label={slots.length > 1 ? `Upload ${slot.label}` : slot.label} accept={slot.accept} multiple={slot.multiple}
                    files={slotFiles[slot.key] || []} setFiles={(selected) => setSlotFiles({ ...slotFiles, [slot.key]: selected })}
                    inputRef={(input) => { fileInputs.current[slot.key] = input }} />)}
                {uploading ?
                    <div className='animBtn'><div className='gSignIn-Anim'></div></div>
                    : <button type="button" className="btn btn-primary f-12" onClick={(e) => {
                        e.preventDefault()
                        upload()
                    }}><i className="fas fa-upload mr-2"></i>Upload</button>}
            </div>

            {error ? <p className="text-danger f-12 mt-2 mb-0">{error}</p> : null}

            <div className="mt-3">
                {loading && files.length === 0 ? <p className="f-12 mb-0">Loading...</p>
                    : lineItems.length === 0 ? <p className="f-12 mb-0 text-custom-grey">No files uploaded.</p>
                        : lineItems.map((item, index) => <div key={item.key} className="border-bottom py-2 f-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="font-weight-bold text-break">{fields.length ? describe(fields, item.data) : item.files[0]?.file_original_name}</span>
                                <span className="text-nowrap">
                                    {fileType.sequence ? <>
                                        <i className={`fa fa-arrow-up mr-2 ${index === 0 ? "text-muted" : "curPoint text-primary"}`} title="Move up" onClick={() => moveLineItem(index, -1)}></i>
                                        <i className={`fa fa-arrow-down mr-2 ${index === lineItems.length - 1 ? "text-muted" : "curPoint text-primary"}`} title="Move down" onClick={() => moveLineItem(index, 1)}></i>
                                    </> : null}
                                    {slots.length === 1 ? <FileButtons item={item.files[0]} actions={actions} /> : null}
                                    {actions.busyId === item.key ? <i className="fas fa-spinner fa-spin"></i>
                                        : <i className="fa fa-trash curPoint text-danger" title="Delete" onClick={() => deleteLineItem(item)}></i>}
                                </span>
                            </div>
                            {slots.length > 1 ?
                                <div className="mt-1">{item.files.map((file) => <FileButtons key={file.id} item={file} label={slotLabel(file) || file.file_original_name} actions={actions} />)}</div>
                                : fields.length ? <div className="text-break">{item.files[0]?.file_original_name}</div> : null}
                        </div>)}
            </div>
        </div>
    )
}

function CompanyFileGroups({ companyId, fileType }) {

    const { token, setShowSuccess } = useLogin()
    const groupFileType = { key: fileType.key, accept: fileType.accept, fields: [], sequence: false }

    const [groups, setGroups] = useState([])
    const [loading, setLoading] = useState(false)
    const [values, setValues] = useState(emptyValues(fileType.groupFields))
    const [documents, setDocuments] = useState([])
    const [creating, setCreating] = useState(false)
    const [busyId, setBusyId] = useState("")
    const [openGroupId, setOpenGroupId] = useState("")
    const [error, setError] = useState("")
    const documentInput = useRef(null)
    const handleApiError = useApiErrorHandler(setError)
    const groupLabel = fileType.groupLabel.toLowerCase()

    useEffect(() => {
        setGroups([])
        setValues(emptyValues(fileType.groupFields))
        setDocuments([])
        setOpenGroupId("")
        setError("")
        if (companyId) {
            loadGroups()
        }
    }, [companyId, fileType.key])

    async function loadGroups() {
        try {
            setLoading(true)
            let response = await getCompanyFileGroups(token, fileType.key, companyId)
            if (response && response.status === 200) {
                const list = Array.isArray(response.data) ? response.data : []
                setGroups(list)
                return list
            }
            handleApiError(response, `Failed to load ${groupLabel}s.`)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
        return null
    }

    // create the group, then upload the selected documents into it
    async function createGroup() {
        try {
            setCreating(true)
            setError("")
            const previousIds = groups.map((group) => group.id)
            let response = await createCompanyFileGroup(token, fileType.key, companyId, values)
            if (!response || response.status !== 200) {
                handleApiError(response, `Failed to create ${groupLabel}.`)
                return
            }
            let list = await loadGroups()
            const created = (list || []).filter((group) => !previousIds.includes(group.id))
            const newGroupId = created.length ? created.reduce((a, b) => (Number(a.id) > Number(b.id) ? a : b)).id : ""
            if (documents.length && !newGroupId) {
                setError(`${fileType.groupLabel} created but documents could not be attached. Please add them inside the ${groupLabel}.`)
                return
            }
            for (const file of documents) {
                let uploadResponse = await uploadCompanyFile(token, fileType.key, companyId, {}, file, newGroupId)
                if (!uploadResponse || uploadResponse.status !== 200) {
                    handleApiError(uploadResponse, `${fileType.groupLabel} created but failed to upload ${file.name}.`)
                    return
                }
            }
            setValues(emptyValues(fileType.groupFields))
            setDocuments([])
            if (documentInput.current) documentInput.current.value = ""
            setShowSuccess(true)
        } catch (error) {
            console.error(error)
        } finally {
            setCreating(false)
        }
    }

    async function deleteGroup(group) {
        try {
            if (!window.confirm(`Delete ${groupLabel} ${describe(fileType.groupFields, group.data)} and all its documents?`)) return
            setBusyId(group.id)
            let response = await deleteCompanyFileGroup(token, fileType.key, group.id)
            if (response && response.status === 200) {
                setGroups(groups.filter((g) => g.id !== group.id))
                setShowSuccess(true)
            } else {
                handleApiError(response, `Failed to delete ${groupLabel}.`)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setBusyId("")
        }
    }

    return (
        <div>
            <div>
                <FieldInputs fields={fileType.groupFields} values={values} setValues={setValues} />
                <FilePicker label="Upload Documents" accept={fileType.accept} multiple files={documents} setFiles={setDocuments} inputRef={documentInput} />
                {creating ?
                    <div className='animBtn'><div className='gSignIn-Anim'></div></div>
                    : <button type="button" className="btn btn-primary f-12" onClick={(e) => {
                        e.preventDefault()
                        createGroup()
                    }}><i className="fas fa-plus mr-2"></i>Add {fileType.groupLabel}</button>}
            </div>

            {error ? <p className="text-danger f-12 mt-2 mb-0">{error}</p> : null}

            <div className="mt-3">
                {loading && groups.length === 0 ? <p className="f-12 mb-0">Loading...</p>
                    : groups.length === 0 ? <p className="f-12 mb-0 text-custom-grey">No {groupLabel}s added.</p>
                        : groups.map((group) => {
                            const isOpen = openGroupId === group.id
                            return <div key={group.id} className="border rounded p-2 mb-2 f-12">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="font-weight-bold text-break">{describe(fileType.groupFields, group.data)}</span>
                                    <span className="text-nowrap">
                                        <a href="#" className="custom-link mr-3" onClick={(e) => {
                                            e.preventDefault()
                                            setOpenGroupId(isOpen ? "" : group.id)
                                        }}>{isOpen ? "Hide Documents" : "Documents"}</a>
                                        {busyId === group.id ? <i className="fas fa-spinner fa-spin"></i>
                                            : <i className="fa fa-trash curPoint text-danger" title="Delete" onClick={() => deleteGroup(group)}></i>}
                                    </span>
                                </div>
                                {isOpen ? <div className="mt-2 pl-3">
                                    <CompanyFileList companyId={companyId} fileType={groupFileType} groupId={group.id} />
                                </div> : null}
                            </div>
                        })}
            </div>
        </div>
    )
}

export default function CompanyFilesPage({ companyId, fileType }) {
    return fileType.groupFields
        ? <CompanyFileGroups companyId={companyId} fileType={fileType} />
        : <CompanyFileList companyId={companyId} fileType={fileType} />
}
